import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Award, Download, Loader2, RefreshCw, IndianRupee, Layers, CalendarCheck, Presentation, TrendingUp, ArrowUpDown, Info, SlidersHorizontal, MessageSquarePlus, Users2, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { PageHeader, Stat } from './demo/DemoUI';
import TrainerPerformanceSheet from './TrainerPerformanceSheet';
import PerformanceTargetsDialog from './PerformanceTargetsDialog';
import PerformanceNoteDialog from './PerformanceNoteDialog';
import PerformancePeriodPicker from './PerformancePeriodPicker';
import PerformanceCategoryView from './PerformanceCategoryView';

const inr = (n) => (n || n === 0 ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—');
const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`);

const BAND_CLS = {
  A: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  B: 'bg-sky-50 text-sky-700 border-sky-200',
  C: 'bg-amber-50 text-amber-700 border-amber-200',
  D: 'bg-rose-50 text-rose-700 border-rose-200',
  '—': 'bg-slate-100 text-slate-500 border-slate-200',
};

const COLS = [
  { k: 'name', l: 'Trainer', align: 'left' },
  { k: 'index', l: 'Index' },
  { k: 'business_ex_gst', l: 'Business excl. GST', money: true, strong: true },
  { k: 'collection_pct', l: 'Coll %', pct: true },
  { k: 'batches', l: 'Batches' },
  { k: 'batches_completed', l: 'Completed' },
  { k: 'classes_held', l: 'Classes Held / Total' },
  { k: 'dpr_pct', l: 'Class Delivery %', pct: true },
  { k: 'attendance_pct', l: 'Attend %', pct: true },
  { k: 'demos', l: 'Demos' },
  { k: 'conversion_pct', l: 'Conv %', pct: true },
];

// the detailed view — every number we hold, nothing scored
const RAW_GROUPS = [
  {
    g: 'Business', cols: [
      { k: 'students', l: 'Student seats' },
      { k: 'students_unique', l: 'Unique students' },
      { k: 'students_billed', l: 'Billed in period' },
      { k: 'invoices', l: 'Invoices' },
      { k: 'business_ex_gst', l: 'Billed excl. GST', money: true },
      { k: 'collected', l: 'Collected', money: true },
      { k: 'pending', l: 'Pending', money: true },
      { k: 'collection_pct', l: 'Coll %', pct: true },
      { k: 'demo_revenue_ex_gst', l: 'Demo revenue excl. GST', money: true },
    ],
  },
  {
    g: 'Delivery', cols: [
      { k: 'batches', l: 'Batches' },
      { k: 'batches_completed', l: 'Completed' },
      { k: 'batches_running', l: 'Running' },
      { k: 'batches_on_hold', l: 'On hold' },
      { k: 'shared_batches', l: 'Shared' },
      { k: 'courses', l: 'Courses' },
      { k: 'avg_class_size', l: 'Avg class size' },
      { k: 'days_logged', l: 'Days logged' },
      { k: 'classes_held', l: 'Classes held' },
      { k: 'classes_missed', l: 'Cancelled' },
      { k: 'classes_excused', l: 'Excused' },
      { k: 'dpr_pct', l: 'Class delivery %', pct: true },
      { k: 'syllabus_pct', l: 'Syllabus %', pct: true },
      { k: 'batches_with_syllabus', l: 'Batches tracked' },
      { k: 'attendance_pct', l: 'Attendance %', pct: true },
      { k: 'months_measured', l: 'Months with data' },
    ],
  },
  {
    g: 'Demos', cols: [
      { k: 'demos', l: 'Valid demos' },
      { k: 'demo_admissions', l: 'Admissions' },
      { k: 'conversion_pct', l: 'Conv %', pct: true },
      { k: 'demo_lost', l: 'Not conducted' },
      { k: 'slot_loss_pct', l: 'Slot loss %', pct: true },
    ],
  },
];
const RAW_COLS = RAW_GROUPS.flatMap((g) => g.cols);

const csvOf = (rows, cols) => {
  const head = ['Rank', 'Trainer', 'Employee ID', ...cols.map((c) => c.l), 'Index', 'Band'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r, i) => [i + 1, r.display_name || r.name, r.employee_id,
    ...cols.map((c) => r[c.k]), r.index, r.band].map(esc).join(','));
  return [head.map(esc).join(','), ...body].join('\n');
};

export default function TrainerPerformancePage() {
  const { user } = useAuth();
  const [fy, setFy] = useState('');
  const [period, setPeriod] = useState({ kind: 'fy' });
  const [picked, setPicked] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ k: 'index', dir: -1 });
  const [openId, setOpenId] = useState(null);
  const [selfView, setSelfView] = useState(false);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [view, setView] = useState('scorecard');
  const [noteFor, setNoteFor] = useState(null);
  const [noteTick, setNoteTick] = useState(0);
  const [bulkBusy, setBulkBusy] = useState('');

  const periodParams = useMemo(() => ({
    fy: fy || undefined,
    month: period.kind === 'month' ? period.month : undefined,
    quarter: period.kind === 'quarter' ? period.quarter : undefined,
    date_from: period.kind === 'custom' ? period.date_from : undefined,
    date_to: period.kind === 'custom' ? period.date_to : undefined,
  }), [fy, period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/trainer-performance', {
        params: { ...periodParams, trainers: picked.length ? picked.join(',') : undefined },
      });
      setData(r.data);
      if (!fy) setFy(r.data.fy);
      setSelfView(!!r.data.self_view);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load the performance review'); }
    finally { setLoading(false); }
  }, [periodParams, picked]);
  useEffect(() => { load(); }, [load]);

  const onPeriod = useCallback((p) => {
    if (p.fyChanged) { setFy(p.fy); setPeriod({ kind: 'fy' }); return; }
    setPeriod((prev) => (JSON.stringify(prev) === JSON.stringify(p) ? prev : p));
  }, []);

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    const { k, dir } = sort;
    return list.sort((a, b) => {
      const av = a[k]; const bv = b[k];
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return ((av ?? -1) - (bv ?? -1)) * dir;
    });
  }, [data, sort]);

  const t = data?.totals || {};
  const wt = data?.weights || {};
  const blockW = {
    business: (wt.business_value || 0) + (wt.collection || 0),
    delivery: (wt.dpr || 0) + (wt.syllabus || 0) + (wt.attendance || 0),
    demos: wt.conversion || 0,
  };
  const toggleSort = (k) => setSort((p) => ({ k, dir: p.k === k ? -p.dir : -1 }));

  const toggleSelfView = async (v) => {
    setSelfView(v);
    try { await api.put('/training/trainer-performance/self-view', { enabled: v }); toast.success(v ? 'Trainers can now see their own delivery sheet' : 'Trainer self-view switched off'); }
    catch (e) { setSelfView(!v); toast.error(e.response?.data?.detail || 'Could not change the setting'); }
  };

  const downloadCsv = () => {
    const blob = new Blob([csvOf(rows, view === 'raw' ? RAW_COLS : COLS.slice(2))], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `trainer-performance-${view}-${data?.period_slug || fy}.csv`;
    a.click();
  };

  const bulkPdf = async (variant) => {
    setBulkBusy(variant);
    try {
      const r = await api.get('/training/trainer-performance/bulk-pdf', {
        params: { ...periodParams, variant, trainers: picked.length ? picked.join(',') : undefined },
        responseType: 'blob',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `performance-review-${rows.length}-trainers_${data?.period_slug || fy}_${variant === 'trainer' ? 'Trainer_Copies' : 'Management'}.pdf`;
      a.click();
      toast.success(`${rows.length} review${rows.length === 1 ? '' : 's'} downloaded — one page each`);
    } catch { toast.error('Bulk PDF export failed'); }
    finally { setBulkBusy(''); }
  };

  const names = data?.trainer_options || [];  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="space-y-6" data-testid="trainer-performance-page">
      <PageHeader icon={Award} eyebrow="Trainers" title="Trainer Performance Review"
        subtitle="Financial-year view of delivery, business handled and demo conversion — the sheet to run an appraisal or a monthly 1:1 on."
        testid="performance-header">
        <Button variant="outline" size="sm" className="h-10" onClick={() => setNoteFor({})} data-testid="perf-note-btn">
          <MessageSquarePlus className="w-4 h-4 mr-1.5" />Review note
        </Button>
        <Button variant="outline" size="sm" className="h-10" onClick={() => setTargetsOpen(true)} data-testid="perf-targets-btn">
          <SlidersHorizontal className="w-4 h-4 mr-1.5" />Targets
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-10" disabled={!rows.length || !!bulkBusy} data-testid="perf-bulk-pdf-btn">
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <span className="mr-1.5 px-1 py-[1px] rounded-[3px] bg-rose-600 text-white text-[8.5px] font-bold tracking-wide">PDF</span>}
              All reviews
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 bg-white p-2" align="end" data-testid="perf-bulk-pdf-popover">
            <p className="px-2 py-1 text-[11px] text-slate-500">
              One page per trainer, merged into a single PDF — {rows.length} trainer(s) for {data?.period_slug || fy}
              {picked.length ? ' (your current selection)' : ''}.
            </p>
            <button onClick={() => bulkPdf('manager')} disabled={!!bulkBusy}
              className="w-full text-left px-2 py-2 rounded hover:bg-slate-50 text-[12.5px] text-slate-700" data-testid="perf-bulk-pdf-manager">
              <b>Management copies</b><span className="block text-[11px] text-slate-500">Business, index, rating and sign-off</span>
            </button>
            <button onClick={() => bulkPdf('trainer')} disabled={!!bulkBusy}
              className="w-full text-left px-2 py-2 rounded hover:bg-slate-50 text-[12.5px] text-slate-700" data-testid="perf-bulk-pdf-trainer">
              <b>Trainer copies</b><span className="block text-[11px] text-slate-500">Delivery only — no business or ranking figures</span>
            </button>
          </PopoverContent>
        </Popover>
        <Button variant="outline" size="sm" className="h-10" onClick={downloadCsv} disabled={!rows.length} data-testid="perf-export-csv">
          <Download className="w-4 h-4 mr-1.5" />CSV
        </Button>
        <Button variant="outline" size="sm" className="h-10" onClick={load} disabled={loading} data-testid="perf-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </PageHeader>

      {/* Controls */}
      <Card className="p-4 border flex flex-wrap items-center gap-3" data-testid="perf-controls">
        <PerformancePeriodPicker fy={fy} fyOptions={data?.fy_options || []} months={data?.months || []}
          currentQuarter={data?.current_quarter || 1} value={period} onChange={onPeriod} />

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-10 text-[13px]" data-testid="perf-trainer-picker">
              <Users2 className="w-3.5 h-3.5 mr-1.5" />
              {picked.length ? `${picked.length} trainer(s)` : 'All trainers'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 bg-white" data-testid="perf-trainer-popover">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Trainers</span>
              {picked.length > 0 && (
                <button onClick={() => setPicked([])} className="text-[11px] text-violet-600 hover:underline" data-testid="perf-trainer-clear">Clear</button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {names.map((n) => (
                <button key={n.id} onClick={() => togglePick(n.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50 text-left" data-testid={`perf-trainer-opt-${n.name}`}>
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${picked.includes(n.id) ? 'bg-violet-600 border-violet-600' : 'border-slate-300'}`}>
                    {picked.includes(n.id) ? <Check className="w-2.5 h-2.5 text-white" /> : null}
                  </span>
                  <span className="text-[12.5px] text-slate-700 flex-1 truncate">{n.name}</span>
                  {n.employee_id ? <span className="text-[10.5px] text-slate-400">{n.employee_id}</span> : null}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <span className="text-[11.5px] text-slate-500">{data?.period}</span>
        {user?.role === 'admin' && (
          <label className="ml-auto flex items-center gap-2.5 text-[12px] text-slate-600">
            <Switch checked={selfView} onCheckedChange={toggleSelfView} data-testid="perf-self-view-toggle" />
            Let trainers see their own sheet
            <span className="text-[11px] text-slate-400">(delivery + improvement areas only, never business numbers)</span>
          </label>
        )}
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="perf-kpi-rail">
        <Stat icon={IndianRupee} label="Business excl. GST" value={inr(t.business_ex_gst)} tone="emerald" sub={`${t.students ?? 0} students billed`} testid="pkpi-business" />
        <Stat icon={TrendingUp} label="Collected" value={inr(t.collected)} tone="violet" sub={`${inr(t.pending)} still pending`} testid="pkpi-collected" />
        <Stat icon={Layers} label="Batches" value={`${t.batches_completed ?? 0}/${t.batches ?? 0}`} tone="sky" sub="completed of handled" testid="pkpi-batches" />
        <Stat icon={CalendarCheck} label="Classes Held" value={t.classes_held ?? 0} tone="slate" sub="attendance submitted" testid="pkpi-classes" />
        <Stat icon={Presentation} label="Demo Admissions" value={`${t.demo_admissions ?? 0}/${t.demos ?? 0}`} tone="amber" sub={`${t.demo_lost ?? 0} slot(s) lost`} testid="pkpi-demos" />
        <Stat icon={Award} label="Avg Index" value={t.avg_index ?? '—'} tone="violet" sub={`${t.trainers ?? 0} trainers reviewed`} testid="pkpi-index" />
      </div>

      <Card className="px-4 py-3 border border-slate-200 bg-slate-50/70" data-testid="perf-weights-note">
        <p className="text-[11.5px] text-slate-600">
          <b>How the index works:</b> Business {blockW.business} (value handled {wt.business_value ?? '—'} · collection {wt.collection ?? '—'}) ·
          Delivery {blockW.delivery} (class delivery {wt.dpr ?? '—'} · syllabus {wt.syllabus ?? '—'} · attendance {wt.attendance ?? '—'}) ·
          Demos {blockW.demos} (conversion only — volume moved into business as you asked). Business value is graded against the best trainer in the same
          window; the percentages score on their own. Class delivery is classes held against classes cancelled without an approved reason,
          and attendance is the present+late share of the marks they submitted. Anything we have no data for drops out and the remaining
          weights are re-balanced, so nobody is punished for a feature the team hasn’t started using. Click any row for the month-by-month sheet.
          {t.shared_batches ? <> Where a course is shared between trainers, business is split by the classes each one actually taught
            ({t.shared_batches} shared batch(es) this period).</> : <> Where a course is shared between trainers, business splits by the
            classes each one actually taught.</>}
          <Popover>
            <PopoverTrigger asChild>
              <button className="ml-1.5 inline-flex items-center gap-1 text-violet-600 font-semibold hover:underline align-middle" data-testid="perf-parked-icon">
                <Info className="w-3.5 h-3.5" />more to come
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-white text-[12px] text-slate-600" data-testid="perf-parked-popover">
              <p className="font-semibold text-slate-800 mb-1.5 text-[12.5px]">Parameters we can add later</p>
              <ul className="space-y-1 list-disc pl-4">
                {(data?.parked || []).map((x) => <li key={x}>{x}</li>)}
              </ul>
              <p className="mt-2 text-[11px] text-slate-400">Revisit this list once the appraisal cycle has run once — adding a block only changes the weights, not the data.</p>
            </PopoverContent>
          </Popover>
          {user?.role === 'admin' && (
            <button onClick={() => setTargetsOpen(true)}
              className="ml-1.5 inline-flex items-center gap-1 text-violet-600 font-semibold hover:underline align-middle" data-testid="perf-edit-weights">
              <SlidersHorizontal className="w-3.5 h-3.5" />change the weights
            </button>
          )}
        </p>
      </Card>

      <div className="flex items-center gap-1.5 border-b border-slate-200" data-testid="perf-view-switch">
        {[['scorecard', 'Scorecard'], ['category', 'By Technology'], ['raw', 'All raw numbers']].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-4 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${
              view === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid={`perf-view-${k}`}>{l}</button>
        ))}
        <span className="ml-auto text-[11px] text-slate-400 pb-2">
          {view === 'scorecard' ? 'Key numbers only — click a row for the full sheet'
            : view === 'category' ? 'Grouped by the Books category on their students’ invoices'
              : `${RAW_COLS.length} raw metrics, grouped`}
        </span>
      </div>

      {view === 'category' ? (
        <PerformanceCategoryView periodParams={periodParams} picked={picked} onOpenTrainer={setOpenId} />
      ) : view === 'scorecard' ? (
        <Card className="border overflow-hidden" data-testid="perf-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                  <th className="px-3 py-3 text-left">#</th>
                  {COLS.map((c) => (
                    <th key={c.k} className={`px-3 py-3 whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right'}`}>
                      <button onClick={() => toggleSort(c.k)} className={`inline-flex items-center gap-1 hover:text-violet-700 ${sort.k === c.k ? 'text-violet-700 font-bold' : ''}`} data-testid={`perf-sort-${c.k}`}>
                        {c.l}<ArrowUpDown className="w-2.5 h-2.5" />
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-right">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.name} onClick={() => r.trainer_id && setOpenId(r.trainer_id)}
                    className="border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors" data-testid={`perf-row-${r.name}`}>
                    <td className="px-3 py-3 text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-slate-800">{r.display_name || r.name}</span>
                      <span className="block text-[10px] text-slate-400">
                        {r.employee_id ? `ID ${r.employee_id} · ` : ''}{r.courses} course(s) · {r.days_logged} day(s) logged
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <span className="tabular-nums font-bold text-slate-900">{r.index ?? '—'}</span>
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold border ${BAND_CLS[r.band] || BAND_CLS['—']}`}>{r.band}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(r.business_ex_gst)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{pct(r.collection_pct)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{r.batches}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700 font-semibold">{r.batches_completed}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600" title="Classes held out of every class day the trainer logged (held + cancelled + excused)">
                      <span className="font-semibold text-slate-800">{r.classes_held}</span>
                      <span className="text-slate-400">/{r.classes_total ?? r.classes_held}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{pct(r.dpr_pct)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{pct(r.attendance_pct)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{r.demos}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">{pct(r.conversion_pct)}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); setNoteFor(r); }}
                        className="text-slate-400 hover:text-violet-700" title="Add a monthly review note" data-testid={`perf-note-${r.name}`}>
                        <MessageSquarePlus className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={COLS.length + 2} className="px-4 py-10 text-center text-[12px] text-slate-400" data-testid="perf-empty">No trainer activity in this period.</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={COLS.length + 2} className="px-4 py-10 text-center text-[12px] text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Crunching the numbers…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="border overflow-hidden" data-testid="perf-raw-card">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70" data-testid="raw-legend">
            <p className="text-[11.5px] text-slate-600">
              <b>Why seats, students and invoices differ:</b> <b>student seats</b> counts a student once for every batch of
              theirs you see here, <b>unique students</b> de-duplicates the same person across batches, <b>billed in period</b>
              is how many of them actually have an invoice dated inside this window, and <b>invoices</b> is the invoice count
              itself — a student on instalments raises two or three, while a student billed in an earlier period keeps
              attending without adding one. Only live invoices count; void, draft and rejected ones are ignored.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr className="bg-slate-50 text-[9.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10"></th>
                  {RAW_GROUPS.map((g) => (
                    <th key={g.g} colSpan={g.cols.length} className="px-3 py-2 text-center border-l border-slate-200 font-bold text-slate-600">{g.g}</th>
                  ))}
                </tr>
                <tr className="bg-slate-50 text-[9.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2.5 text-left sticky left-0 bg-slate-50 z-10 min-w-[170px]">Trainer</th>
                  {RAW_GROUPS.flatMap((g, gi) => g.cols.map((c, ci) => (
                    <th key={c.k} className={`px-3 py-2.5 text-right whitespace-nowrap ${ci === 0 && gi > 0 ? 'border-l border-slate-200' : ''}`}>
                      <button onClick={() => toggleSort(c.k)} className={`inline-flex items-center gap-1 hover:text-violet-700 ${sort.k === c.k ? 'text-violet-700 font-bold' : ''}`} data-testid={`raw-sort-${c.k}`}>
                        {c.l}<ArrowUpDown className="w-2.5 h-2.5" />
                      </button>
                    </th>
                  )))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name} className="border-b border-slate-100 last:border-0 hover:bg-violet-50/60" data-testid={`raw-row-${r.name}`}>
                    <td className="px-3 py-2.5 sticky left-0 bg-white z-10">
                      <button onClick={() => r.trainer_id && setOpenId(r.trainer_id)} className="text-left">
                        <span className="font-semibold text-slate-800 hover:text-violet-700">{r.display_name || r.name}</span>
                        <span className="block text-[10px] text-slate-400">{r.employee_id ? `ID ${r.employee_id}` : '—'}</span>
                      </button>
                    </td>
                    {RAW_GROUPS.flatMap((g, gi) => g.cols.map((c, ci) => (
                      <td key={c.k} className={`px-3 py-2.5 text-right tabular-nums text-slate-600 ${ci === 0 && gi > 0 ? 'border-l border-slate-200' : ''}`}>
                        {c.money ? inr(r[c.k]) : c.pct ? pct(r[c.k]) : (r[c.k] ?? '—')}
                      </td>
                    )))}
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={RAW_COLS.length + 1} className="px-4 py-10 text-center text-[12px] text-slate-400" data-testid="raw-empty">No trainer activity in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <TrainerPerformanceSheet trainerId={openId} fy={fy} onClose={() => setOpenId(null)} onNote={(tr) => setNoteFor(tr)}
        onEditWeights={() => setTargetsOpen(true)} refreshKey={noteTick} />
      <PerformanceTargetsDialog open={targetsOpen} isAdmin={user?.role === 'admin'} onClose={() => setTargetsOpen(false)} onSaved={load} />
      <PerformanceNoteDialog open={!!noteFor} trainer={noteFor} fy={fy} months={data?.months || []}
        trainerOptions={names} onClose={() => setNoteFor(null)} onSaved={() => { setNoteTick((n) => n + 1); load(); }} />
    </div>
  );
}
